import {
  ActionIcon,
  AppShell,
  Box,
  Button,
  Divider,
  Group,
  NavLink,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';

import type { ComponentType } from 'react';

import { IconTileView } from '../../common/atoms/IconTile';
import { StatusDotView } from '../../common/atoms/StatusDot';
import type { AppSidebarNavItem, AppSidebarProps } from './AppSidebar.types';

const NAV_ICON_SIZE = 14;

function AppSidebarNavLink({
  item,
  onNavigate,
}: {
  item: AppSidebarNavItem;
  onNavigate: (href: string) => void;
}) {
  const Icon = item.icon;
  const subList = item.subList;

  return (
    <Stack gap={4}>
      <NavLink
        leftSection={
          <IconTileView
            icon={Icon as ComponentType<{ size?: string | number; stroke?: number }>}
            iconSize={NAV_ICON_SIZE}
            color="brand"
          />
        }
        active={item.active}
        variant={item.active ? 'light' : 'subtle'}
        label={item.label}
        onClick={() => onNavigate(item.href)}
        fw={item.active ? 600 : 400}
        style={{ borderRadius: 'var(--mantine-radius-sm)' }}
      />

      {subList && (
        <Stack gap={2} pr={4}>
          <Box pl={26} style={{ marginLeft: 14, borderLeft: '1px solid var(--mantine-color-gray-3)' }}>
            <Stack gap={2}>
              {subList.loading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} height={28} radius="sm" />
                ))
              ) : subList.items.length === 0 ? (
                <Text size="xs" c="dimmed" py={4}>
                  {subList.emptyLabel}
                </Text>
              ) : (
                subList.items.map((sub) => (
                  <NavLink
                    key={sub.key}
                    leftSection={sub.color ? <StatusDotView color={sub.color} size={7} /> : undefined}
                    active={sub.active}
                    variant={sub.active ? 'light' : 'subtle'}
                    label={
                      <Text size="sm" truncate="end">
                        {sub.label}
                      </Text>
                    }
                    onClick={() => onNavigate(sub.href)}
                    fw={sub.active ? 600 : 400}
                    py={6}
                    style={{ borderRadius: 'var(--mantine-radius-sm)' }}
                  />
                ))
              )}
            </Stack>
          </Box>

          {subList.onQuickAdd && (
            <Box pl={30}>
              <Button
                variant="outline"
                color="brand"
                size="xs"
                leftSection={<IconPlus size={14} stroke={1.5} />}
                onClick={subList.onQuickAdd}
                fullWidth
                style={{ borderStyle: 'dashed' }}
              >
                {subList.quickAddLabel}
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Primary app navigation. Presentational only — sections/items/active state
 * and routing all live in apps/web. Full icon+label everywhere, no
 * icon-only/collapsed mode and no hidden toggles — our users are on
 * tablets, in a hurry, and shouldn't have to discover an affordance.
 */
export function AppSidebarView({
  logo,
  sections,
  footerItems = [],
  onNavigate,
  opened,
  onClose,
  loading = false,
}: AppSidebarProps) {
  return (
    <>
      <AppShell.Section
        style={{ borderBottom: '1px solid var(--mantine-color-gray-3)', height: 51 }}
      >
        <Group justify="space-between" wrap="nowrap" h="100%" px="xs">
          {logo}
          <ActionIcon
            variant="subtle"
            c="dimmed"
            size="md"
            hiddenFrom="sm"
            aria-label="Luk menu"
            aria-expanded={opened}
            onClick={onClose}
          >
            <IconX size={18} stroke={1.5} />
          </ActionIcon>
        </Group>
      </AppShell.Section>

      <AppShell.Section grow component={ScrollArea}>
        <Stack gap={0} py="xs">
          {loading ? (
            <Stack gap="xs" px="xs">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} height={32} radius="sm" />
              ))}
            </Stack>
          ) : sections.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="xl">
              Ingen menupunkter tilgængelige
            </Text>
          ) : (
            <Stack gap="sm" px="xs">
              {sections.flatMap((section) =>
                section.items.map((item) => (
                  <AppSidebarNavLink key={item.key} item={item} onNavigate={onNavigate} />
                ))
              )}
            </Stack>
          )}
        </Stack>
      </AppShell.Section>

      {footerItems.length > 0 && (
        <AppShell.Section>
          <Divider />
          <Stack gap="sm" px="xs" py="xs">
            {footerItems.map((item) => (
              <AppSidebarNavLink key={item.key} item={item} onNavigate={onNavigate} />
            ))}
          </Stack>
        </AppShell.Section>
      )}
    </>
  );
}
